import {
  type CSSProperties,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import styles from './Avatar.module.css'
import { observer } from 'mobx-react-lite'
import {
  Skeleton,
  type SkeletonColor,
  useIsSkeleton,
} from '@/components/Skeleton'
import { useImageLoadingState } from '@/contexts/image-context'

/**
 * The subject whos avatar is on display.
 */
export interface AvatarSubject {
  avatarUrl?: string | null
  displayName?: string | null
}

export interface AvatarProps {
  /**
   * Whether to render a skeleton.
   */
  isSkeleton?: boolean

  /**
   * Color to use for the skeleton.
   */
  skeletonColor?: SkeletonColor

  /**
   * Avatar size.
   */
  size?: 24 | 28 | 40 | 48 | 56 | 64

  /**
   * The subject being displayed.
   */
  subject: AvatarSubject

  /**
   * If specified, shows a presence indicator.
   *
   * @default 'none'
   */
  presence?: 'online' | 'offline' | 'none'

  /**
   * Whether to show a surface-colored ring around the avatar.
   */
  ring?: boolean
}

/**
 * Displays an avatar of a user, or a fallback if no avatar is available.
 */
export const Avatar = observer(function Avatar({
  subject,
  isSkeleton,
  skeletonColor,
  size = 48,
  presence = 'none',
  ring,
}: AvatarProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const src = subject.avatarUrl
  const prevSrcRef = useRef(src)
  const alt = subject.displayName
    ? `Avatar of ${subject.displayName}`
    : 'Avatar'

  const [usingFallback, setUsingFallback] = useState(false)
  const onImageLoadError = useCallback(() => setUsingFallback(true), [])

  // When the source changes, reset the fallback.
  if (prevSrcRef.current !== src) {
    prevSrcRef.current = src
    setUsingFallback(false)
  }

  const fallbackText = useMemo(
    () =>
      subject.displayName
        ? getInitials(subject.displayName).toLocaleUpperCase()
        : '',
    [subject.displayName],
  )

  return (
    <div
      className={cx(styles.root, ring && styles.ring)}
      style={{ '--avatar-size': `${size}px` } as CSSProperties}
    >
      {isSkeleton ? (
        <Skeleton color={skeletonColor} variant="circle" height={size} />
      ) : src && !usingFallback ? (
        <AvatarImage alt={alt} src={src} onImageLoadFailed={onImageLoadError} />
      ) : (
        <Fallback text={fallbackText} />
      )}
      {!isSkeleton && presence !== 'none' && (
        <PresenceIndicator presence={presence} />
      )}
    </div>
  )
})

interface AvatarImageProps {
  src: string
  alt: string
  onImageLoaded?: () => unknown
  onImageLoadFailed?: () => unknown
}

/**
 * Avatar Image
 */
function AvatarImage({ src, alt, onImageLoadFailed }: AvatarImageProps) {
  const { loaded, onLoad } = useImageLoadingState({ src })

  return (
    <>
      {!loaded && (
        <Skeleton
          variant="circle"
          width="var(--avatar-size)"
          height="var(--avatar-size)"
        />
      )}
      <img
        src={src}
        alt={alt}
        className={cx(styles.avatarImage, !loaded && 'sr-only')}
        onError={onImageLoadFailed}
        onLoad={loaded ? undefined : onLoad}
      />
    </>
  )
}

interface FallbackProps {
  text: string
}

/**
 * Fallback
 */
function Fallback({ text }: FallbackProps) {
  return <div className={styles.fallback}>{text}</div>
}

interface PresenceIndicatorProps {
  presence: 'online' | 'offline'
}

/**
 * Presence Indicator
 */
function PresenceIndicator({ presence }: PresenceIndicatorProps) {
  return (
    <div
      className={cx(
        styles.presenceIndicator,
        presence === 'online' && styles.online,
      )}
      aria-label={presence}
    />
  )
}

/**
 * Gets initials from a string representing a name or email.
 * @param str
 */
function getInitials(str: string) {
  if (str.includes('@')) {
    // It's an email, take the first and last part.
    const [, user, domain] = Array.from(/(.*?)@(.*)/.exec(str) ?? [])
    return `${user.charAt(0)}${domain.charAt(0)}`
  }

  const [first, last] = str.split(' ')
  if (!last) {
    return first.charAt(0)
  }

  return `${first.charAt(0)}${last.charAt(0)}`
}
